<template>
  <div
    class="stat-card"
    :class="`stat-card--${variant}`"
    ref="cardRef"
  >
    <!-- Icon -->
    <div class="stat-card-icon" :class="`stat-card-icon--${variant}`">
      <component :is="icon" class="h-5 w-5" />
    </div>

    <!-- Value + badge row -->
    <div class="stat-card-value-row">
      <p class="stat-card-value" ref="valueRef">
        <slot name="value">{{ animatedValue }}</slot>
      </p>
      <span
        v-if="badge"
        class="stat-card-badge"
        :class="`stat-card-badge--${badgeVariant}`"
      >
        <component
          v-if="badgeVariant === 'up'"
          :is="TrendingUpIcon"
          class="h-3 w-3"
        />
        <component
          v-else-if="badgeVariant === 'down'"
          :is="TrendingDownIcon"
          class="h-3 w-3"
        />
        {{ badge }}
      </span>
      <slot name="badge" />
    </div>

    <!-- Label -->
    <p class="stat-card-label">
      <slot name="label">{{ label }}</slot>
    </p>

    <!-- Footer meta -->
    <p v-if="footer || $slots.footer" class="stat-card-footer">
      <slot name="footer">{{ footer }}</slot>
    </p>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue'
import type { Component } from 'vue'
import { TrendingUp, TrendingDown } from 'lucide-vue-next'

const TrendingUpIcon = TrendingUp
const TrendingDownIcon = TrendingDown

const props = withDefaults(defineProps<{
  variant?: 'primary' | 'success' | 'warning' | 'info'
  icon: Component
  label?: string
  value?: string | number
  footer?: string
  badge?: string
  badgeVariant?: 'up' | 'down' | 'neutral'
  animateValue?: boolean
}>(), {
  variant: 'primary',
  label: '',
  value: '',
  footer: '',
  badge: '',
  badgeVariant: 'up',
  animateValue: true
})

// --------------- Animated counter ---------------
const cardRef = ref<HTMLElement>()
const valueRef = ref<HTMLElement>()
const displayValue = ref('0')
const hasAnimated = ref(false)

const numericValue = computed(() => {
  if (typeof props.value === 'number') return props.value
  const parsed = parseFloat(String(props.value).replace(/[^0-9.-]/g, ''))
  return isNaN(parsed) ? null : parsed
})

const isNumeric = computed(() => numericValue.value !== null)

const animatedValue = computed(() => {
  if (!props.animateValue || !isNumeric.value) return props.value
  return displayValue.value
})

function formatNumber(num: number): string {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
  if (num >= 1000) return num.toLocaleString('en-US')
  return num.toString()
}

function animateCount(target: number, duration = 800) {
  if (hasAnimated.value) {
    displayValue.value = formatNumber(target)
    return
  }
  hasAnimated.value = true

  const start = 0
  const startTime = performance.now()

  function step(currentTime: number) {
    const elapsed = currentTime - startTime
    const progress = Math.min(elapsed / duration, 1)
    const eased = 1 - Math.pow(1 - progress, 3)
    const current = Math.round(start + (target - start) * eased)
    displayValue.value = formatNumber(current)

    if (progress < 1) {
      requestAnimationFrame(step)
    }
  }

  requestAnimationFrame(step)
}

watch(() => props.value, () => {
  if (numericValue.value !== null && props.animateValue) {
    hasAnimated.value = false
    animateCount(numericValue.value)
  }
})

onMounted(() => {
  if (!props.animateValue || !isNumeric.value) return
  if (!cardRef.value) return

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && numericValue.value !== null) {
          animateCount(numericValue.value)
          observer.unobserve(entry.target)
        }
      })
    },
    { threshold: 0.3 }
  )

  observer.observe(cardRef.value)
})
</script>

<style scoped>
/* ============================================
   STAT CARD
   ============================================ */

.stat-card {
  @apply relative overflow-hidden
         bg-white dark:bg-gray-800/90
         rounded-2xl
         border border-gray-200 dark:border-gray-700/80
         p-5
         transition-shadow duration-200 ease-out;
}

.stat-card:hover {
  @apply shadow-md dark:shadow-gray-900/40;
}

/* ============================================
   ICON
   ============================================ */

.stat-card-icon {
  @apply h-10 w-10 flex items-center justify-center
         rounded-lg
         mb-4;
}

.stat-card-icon--primary { @apply bg-primary/10 text-primary dark:bg-primary-dark/15 dark:text-primary-dark; }
.stat-card-icon--success { @apply bg-success/10 text-success dark:bg-success-dark/15 dark:text-success-dark; }
.stat-card-icon--warning { @apply bg-warning/10 text-warning dark:bg-warning-dark/15 dark:text-warning-dark; }
.stat-card-icon--info    { @apply bg-info/10 text-info dark:bg-info-dark/15 dark:text-info-dark; }

/* ============================================
   VALUE ROW (value + badge)
   ============================================ */

.stat-card-value-row {
  @apply flex items-baseline gap-2 mb-1;
}

.stat-card-value {
  @apply text-[1.75rem] font-bold leading-tight
         text-gray-900 dark:text-white
         tabular-nums tracking-tight;
}

/* ============================================
   BADGE
   ============================================ */

.stat-card-badge {
  @apply inline-flex items-center gap-1
         px-2 py-0.5
         text-[11px] font-semibold
         rounded-full;
}

.stat-card-badge--up {
  @apply bg-success/10 text-success
         dark:bg-success-dark/15 dark:text-success-dark;
}

.stat-card-badge--down {
  @apply bg-danger/10 text-danger
         dark:bg-danger-dark/15 dark:text-danger-dark;
}

.stat-card-badge--neutral {
  @apply bg-gray-100 text-gray-500
         dark:bg-gray-700/60 dark:text-gray-400;
}

/* ============================================
   LABEL
   ============================================ */

.stat-card-label {
  @apply text-xs font-medium
         text-gray-500 dark:text-gray-400
         tracking-wide;
}

/* ============================================
   FOOTER
   ============================================ */

.stat-card-footer {
  @apply mt-3 pt-3
         text-xs text-gray-400 dark:text-gray-500
         border-t border-gray-100 dark:border-gray-700/40;
}
</style>
